import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft, Loader2, ChevronDown, Bell, Mail, Sparkles, X,
  Phone, AtSign, MapPin, Pencil, FileText, Calendar as CalendarIcon,
  Building2, Stethoscope, Beaker, AlertCircle, CheckCircle2,
  CircleDot, Circle, Clock, Link2, DollarSign, Wand2, Edit3,
  Archive, ArchiveRestore, Trash2, Undo2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

import { SendPaymentButton, PaymentPill } from "@/components/BillingPanel";
import OpsAnalysisPanel from "@/components/dispatch/OpsAnalysisPanel";
import LeadEditDialog from "@/components/LeadEditDialog";
import LeadCommunicationsPanel from "@/components/lead/LeadCommunicationsPanel";
import {
  getLead, getOpsAnalysis, updateLeadBilling, updateLeadStatus,
  archiveLead, restoreLead, binLead, unbinLead, getAdminToken, getAdminUser,
} from "@/lib/api";

// =============================================================================
// Status / pipeline labels
// =============================================================================
const STAGE_LABELS = ["Intake", "Review", "Scheduling", "Payment", "Dispatch", "Complete"];

const deriveStageIndex = (lead) => {
  // Map operational + payment status → 0..5 stepper index
  const s = (lead?.status || "new").toLowerCase();
  const ps = (lead?.payment_status || "unpaid").toLowerCase();
  if (s === "completed") return 5;
  if (s === "scheduled" || s === "in_progress") return 4;
  if (ps === "paid" || ps === "partial_payment") return 4; // payment received → dispatch ready
  if (ps === "payment_link_sent" || ps === "custom_payment_created") return 3;
  if ((lead?.amount_due || 0) > 0) return 2; // priced → ready for payment/scheduling
  if (s === "contacted") return 1;
  return 0; // intake
};

const fmtMoney = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

// =============================================================================
// Reusable Card
// =============================================================================
const Card = ({ title, icon: Icon, action, gold, children, testId }) => (
  <section
    className="rounded-xl border bg-white"
    style={{
      borderColor: gold ? "rgba(212,175,55,0.55)" : "#E2E8F0",
      boxShadow: gold ? "0 0 0 1px rgba(212,175,55,0.15)" : undefined,
    }}
    data-testid={testId}
  >
    <header className="px-5 py-3.5 flex items-center justify-between gap-2 border-b" style={{ borderColor: gold ? "rgba(212,175,55,0.35)" : "#E2E8F0" }}>
      <div className="flex items-center gap-2">
        {Icon ? (
          <span
            className="grid place-items-center h-6 w-6 rounded"
            style={{ background: gold ? "rgba(212,175,55,0.18)" : "rgba(11,37,69,0.06)", color: gold ? "#7A5E12" : "#0B2545" }}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <h3 className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>{title}</h3>
      </div>
      {action}
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#94A3B8" }}>
      {label}
    </div>
    <div className="text-sm" style={{ color: "#0B2545" }}>{children}</div>
  </div>
);

// =============================================================================
// Workflow stepper
// =============================================================================
const Stepper = ({ activeIndex }) => (
  <nav className="flex items-center gap-1.5 flex-wrap" data-testid="lead-stepper">
    {STAGE_LABELS.map((label, i) => {
      const isActive = i === activeIndex;
      const isPast = i < activeIndex;
      return (
        <React.Fragment key={label}>
          <span
            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={{
              background: isActive ? "#0B2545" : isPast ? "rgba(15,109,93,0.10)" : "transparent",
              color: isActive ? "#FFFFFF" : isPast ? "#0F6D5D" : "#64748B",
              border: isActive ? "1px solid #0B2545" : isPast ? "1px solid rgba(15,109,93,0.30)" : "1px solid transparent",
            }}
            data-testid={`stage-${label.toLowerCase()}`}
          >
            {label}
          </span>
          {i < STAGE_LABELS.length - 1 ? (
            <span className="text-[#CBD5E1] text-xs select-none">›</span>
          ) : null}
        </React.Fragment>
      );
    })}
  </nav>
);

// =============================================================================
// AI Pricing card (compact view of ops_analysis bundle)
// =============================================================================
const AIPricingCard = ({ lead, onApplyPrice, onModifyPrice }) => {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!lead?.id) return;
    setLoading(true);
    getOpsAnalysis(lead.id)
      .then(setBundle)
      .catch(() => setBundle(null))
      .finally(() => setLoading(false));
  }, [lead?.id]);

  const handleAccept = async () => {
    if (!bundle?.pricing?.recommended_total) return;
    setApplying(true);
    try {
      const updated = await updateLeadBilling(lead.id, {
        amount_due: bundle.pricing.recommended_total,
      });
      onApplyPrice?.(updated);
      toast.success(`Price ${fmtMoney(bundle.pricing.recommended_total)} applied.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply price.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card
      title="AI Pricing Recommendation"
      icon={Sparkles}
      gold
      testId="ai-pricing-card"
      action={
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold"
              style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D" }}>
          {bundle?.pricing?.confidence_label || "High Confidence"}
        </span>
      }
    >
      {loading ? (
        <div className="grid place-items-center py-12">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#7A5E12" }} />
        </div>
      ) : !bundle ? (
        <div className="text-sm text-center py-6" style={{ color: "#94A3B8" }}>
          No recommendation available for this lead yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Headline */}
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#64748B" }}>
                Recommended Price
              </div>
              <div className="font-display text-4xl font-bold mt-0.5" style={{ color: "#0F6D5D" }} data-testid="ai-price-headline">
                {fmtMoney(bundle.pricing?.recommended_total)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#64748B" }}>Confidence</div>
              <div className="font-display text-3xl font-bold" style={{ color: "#0F6D5D" }}>
                {bundle.pricing?.confidence_pct || 92}%
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="border-t pt-3" style={{ borderColor: "#E2E8F0" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#64748B" }}>
                Pricing Breakdown
              </span>
              <span className="text-[11px] font-medium" style={{ color: "#0B2545" }}>
                View Calculation
              </span>
            </div>
            <ul className="space-y-1.5 text-sm">
              {(bundle.pricing?.line_items || []).slice(0, 6).map((li, i) => (
                <li key={i} className="flex items-center justify-between" data-testid={`pricing-line-${i}`}>
                  <span style={{ color: "#475569" }}>{li.label}</span>
                  <span className="font-medium" style={{ color: "#0B2545" }}>{fmtMoney(li.amount)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Profit */}
          {bundle.pricing?.estimated_profit != null ? (
            <div className="border-t pt-3 flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
              <span className="text-sm font-semibold" style={{ color: "#0B2545" }}>Estimated Profit</span>
              <span className="text-sm font-bold" style={{ color: "#0F6D5D" }}>
                {fmtMoney(bundle.pricing.estimated_profit)}
                {bundle.pricing.estimated_profit_pct ? <span className="ml-1 font-medium" style={{ color: "#64748B" }}>({bundle.pricing.estimated_profit_pct}%)</span> : null}
              </span>
            </div>
          ) : null}

          {/* Reasoning */}
          {(bundle.pricing?.reasoning || []).length > 0 ? (
            <div className="border-t pt-3" style={{ borderColor: "#E2E8F0" }}>
              <div className="text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: "#64748B" }}>Reasoning</div>
              <ul className="space-y-1.5 text-sm">
                {bundle.pricing.reasoning.map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#0F6D5D" }} />
                    <span style={{ color: "#475569" }}>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* CTAs */}
          <div className="flex flex-col gap-2 pt-1">
            <Button
              onClick={handleAccept}
              disabled={applying || !bundle.pricing?.recommended_total}
              className="h-10 w-full"
              style={{ background: "#0B2545", color: "#FFFFFF" }}
              data-testid="ai-accept-price"
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
              Accept Recommendation
            </Button>
            <Button variant="outline" className="h-10 w-full" onClick={() => onModifyPrice?.()} data-testid="ai-modify-price">
              <Edit3 className="h-3.5 w-3.5 mr-1.5" />
              Modify Price
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// =============================================================================
// Lead Timeline
// =============================================================================
const TimelineCard = ({ lead }) => {
  // Build event list from lead data + activity_log
  const events = useMemo(() => {
    const evts = [];
    if (lead?.created_at) {
      evts.push({
        kind: "lead_created", label: "Lead Created",
        ts: lead.created_at,
        detail: lead.company ? `via ${lead.company}` : "via Website Intake",
        complete: true,
      });
    }
    if (lead?.lab_order_file_id) {
      evts.push({
        kind: "docs_uploaded", label: "Documents Uploaded",
        ts: lead.created_at, detail: lead.lab_order_filename || "Lab order uploaded",
        complete: true,
      });
    }
    const stageIdx = deriveStageIndex(lead);
    if (stageIdx >= 1) {
      evts.push({
        kind: "intake_review", label: "Intake Review",
        ts: lead.updated_at || lead.created_at,
        detail: "Under review",
        complete: stageIdx > 1, current: stageIdx === 1,
      });
    } else {
      evts.push({ kind: "intake_review", label: "Intake Review", detail: "Pending", current: stageIdx === 1, complete: false });
    }
    evts.push({
      kind: "scheduling", label: "Scheduling",
      detail: stageIdx >= 2 ? "Awaiting payment" : "Pending",
      complete: stageIdx > 3, current: stageIdx === 2 || stageIdx === 3,
    });
    evts.push({
      kind: "payment", label: "Payment",
      ts: lead?.payment_received_at || lead?.payment_request_sent_at,
      detail: lead?.payment_received_at
        ? `Received ${fmtMoney(lead.amount_paid)}`
        : lead?.payment_request_sent_at
        ? "Link sent"
        : "Pending",
      complete: stageIdx >= 4 && lead?.payment_status === "paid",
      current: lead?.payment_status === "payment_link_sent",
    });
    evts.push({
      kind: "dispatch", label: "Dispatch",
      detail: stageIdx >= 4 ? "Ready" : "Pending",
      complete: stageIdx === 5, current: stageIdx === 4,
    });
    evts.push({
      kind: "complete", label: "Complete",
      detail: lead?.status === "completed" ? "Job done" : "Pending",
      complete: lead?.status === "completed",
    });
    return evts;
  }, [lead]);

  return (
    <Card title="Lead Timeline" icon={Clock} testId="timeline-card">
      <ol className="relative">
        {events.map((e, i) => {
          const last = i === events.length - 1;
          const color = e.complete ? "#0F6D5D" : e.current ? "#D4AF37" : "#CBD5E1";
          const bg = e.complete ? "rgba(15,109,93,0.10)" : e.current ? "rgba(212,175,55,0.18)" : "#F1F5F9";
          const Icon = e.kind === "docs_uploaded" ? FileText : e.kind === "payment" ? DollarSign : e.kind === "scheduling" ? CalendarIcon : e.kind === "dispatch" ? Wand2 : e.kind === "complete" ? CheckCircle2 : CircleDot;
          return (
            <li key={e.kind} className="relative pl-9 pb-5 last:pb-0" data-testid={`timeline-${e.kind}`}>
              {!last ? (
                <span className="absolute left-3 top-7 bottom-0 w-px" style={{ background: "#E2E8F0" }} />
              ) : null}
              <span
                className="absolute left-0 top-0 grid place-items-center h-6 w-6 rounded-full"
                style={{ background: bg, color }}
              >
                <Icon className="h-3 w-3" />
              </span>
              <div className="text-sm font-semibold" style={{ color: e.complete ? "#0B2545" : e.current ? "#0B2545" : "#94A3B8" }}>
                {e.label}
              </div>
              {e.ts ? (
                <div className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>
                  {format(new Date(e.ts), "MMM d, yyyy h:mm a")}
                </div>
              ) : null}
              <div className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>{e.detail}</div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

// =============================================================================
// Workspace
// =============================================================================
export default function LeadWorkspace() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const isAdmin = !!getAdminUser();

  const handleBack = () => {
    // Prefer history-back so any filters / scroll position on the leads list survive.
    if (window.history.length > 1) navigate(-1);
    else navigate("/admin?tab=consumer");
  };

  // Auth gate
  useEffect(() => {
    if (!getAdminToken()) navigate("/admin", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const data = await getLead(leadId);
      setLead(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load lead.");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const handleLeadUpdate = (updated) => setLead((prev) => ({ ...prev, ...updated }));

  const handleStatusChange = async (status) => {
    try {
      const updated = await updateLeadStatus(leadId, status);
      handleLeadUpdate(updated);
      toast.success(`Status: ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed."); }
  };

  const handleArchive = async () => {
    try { const u = await archiveLead(leadId); handleLeadUpdate(u); toast.success("Archived."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed."); }
  };
  const handleRestore = async () => {
    try { const u = await restoreLead(leadId); handleLeadUpdate(u); toast.success("Restored."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed."); }
  };
  const handleBin = async () => {
    try { const u = await binLead(leadId); handleLeadUpdate(u); toast.success("Moved to bin."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Move to bin failed."); }
  };
  const handleUnbin = async () => {
    try { const u = await unbinLead(leadId); handleLeadUpdate(u); toast.success("Restored from bin."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore from bin failed."); }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F8FAFC" }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
      </div>
    );
  }
  if (!lead) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F8FAFC" }}>
        <div className="text-sm" style={{ color: "#64748B" }}>Lead not found.</div>
      </div>
    );
  }

  const stageIdx = deriveStageIndex(lead);
  const requestId = lead.request_id || `TJC-${(lead.id || "").slice(0, 4).toUpperCase()}`;
  const stageLabel = STAGE_LABELS[stageIdx] || "Intake";

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }} data-testid="lead-workspace">
      {/* Top header */}
      <header
        className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="px-5 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/admin?tab=consumer" onClick={(e) => { e.preventDefault(); handleBack(); }} className="inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: "#0B2545" }} data-testid="back-to-leads">
            <ArrowLeft className="h-4 w-4" />
            Back to Leads
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAiOpen(true)}
              className="h-9 px-3"
              style={{ borderColor: "rgba(212,175,55,0.5)", color: "#7A5E12", background: "rgba(212,175,55,0.08)" }}
              data-testid="open-ai-assistant"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              AI Assistant
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9" data-testid="actions-dropdown">
                  Actions <ChevronDown className="ml-1 h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Operational status</DropdownMenuLabel>
                {["new", "contacted", "scheduled", "in_progress", "completed", "cancelled"].map((s) => (
                  <DropdownMenuItem key={s} onSelect={() => handleStatusChange(s)} data-testid={`status-${s}`}>
                    {s.replace("_", " ")}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {lead.is_binned ? (
                  <>
                    <DropdownMenuItem onSelect={handleUnbin} data-testid="action-unbin"><Undo2 className="h-3.5 w-3.5 mr-1.5" /> Restore from bin</DropdownMenuItem>
                  </>
                ) : lead.is_archived ? (
                  <>
                    <DropdownMenuItem onSelect={handleRestore} data-testid="action-restore"><ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore from archive</DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleBin} data-testid="action-bin"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Move to bin</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onSelect={handleArchive} data-testid="action-archive"><Archive className="h-3.5 w-3.5 mr-1.5" /> Archive</DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleBin} data-testid="action-bin"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Move to bin</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button className="h-9 w-9 grid place-items-center rounded-md hover:bg-slate-100" data-testid="bell">
              <Bell className="h-4 w-4" style={{ color: "#64748B" }} />
            </button>
            <button className="h-9 w-9 grid place-items-center rounded-md hover:bg-slate-100" data-testid="msg">
              <Mail className="h-4 w-4" style={{ color: "#64748B" }} />
            </button>
          </div>
        </div>

        {/* Title + status pill + stepper */}
        <div className="px-5 lg:px-8 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold" style={{ color: "#0B2545" }}>
              Lead #{requestId}
            </h1>
            <span
              className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full"
              style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
              data-testid="stage-pill"
            >
              {stageLabel} Review
            </span>
          </div>
          <Stepper activeIndex={stageIdx} />
        </div>
      </header>

      {/* Body grid */}
      <main className="px-5 lg:px-8 py-6">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* LEFT COLUMN */}
          <div className="space-y-5">
            {/* Client Information */}
            <Card title="Client Information" action={<EditBtn onClick={() => setEditSection("client")} disabled={!isAdmin} />} testId="client-info-card">
              <div className="flex items-start gap-4">
                <div
                  className="grid place-items-center h-14 w-14 rounded-full font-display text-lg font-semibold shrink-0"
                  style={{ background: "rgba(11,37,69,0.08)", color: "#0B2545" }}
                >
                  {initials(lead.full_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>{lead.full_name}</h4>
                    {lead.urgency && lead.urgency !== "Standard" ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D" }}>
                        {lead.urgency}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2" style={{ color: "#475569" }}>
                      <Phone className="h-3.5 w-3.5" style={{ color: "#94A3B8" }} />
                      {lead.phone || "—"}
                    </div>
                    <div className="flex items-center gap-2" style={{ color: "#475569" }}>
                      <AtSign className="h-3.5 w-3.5" style={{ color: "#94A3B8" }} />
                      {lead.email || "—"}
                    </div>
                    {lead.service_address ? (
                      <div className="flex items-start gap-2 pt-1" style={{ color: "#475569" }}>
                        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
                        <span>{lead.service_address}</span>
                      </div>
                    ) : null}
                    {lead.company ? (
                      <div className="flex items-center gap-2" style={{ color: "#475569" }}>
                        <Building2 className="h-3.5 w-3.5" style={{ color: "#94A3B8" }} />
                        {lead.company}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Card>

            {/* Service Details */}
            <Card title="Service Details" action={<EditBtn onClick={() => setEditSection("service")} disabled={!isAdmin} />} testId="service-details-card">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Service Type">
                  <div className="flex flex-wrap gap-1.5">
                    {(lead.services || []).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded border"
                            style={{ borderColor: "#E2E8F0", color: "#0B2545", background: "#F8FAFC" }}>
                        {s}
                      </span>
                    ))}
                    {(!lead.services || lead.services.length === 0) ? "—" : null}
                  </div>
                </Field>
                <Field label="Urgency">{lead.urgency || "Standard"}</Field>
                <Field label="Preferred Date">{lead.preferred_date || "—"}</Field>
                <Field label="Preferred Time">{lead.preferred_time || "—"}</Field>
                {lead.service_intake?.lab ? (
                  <Field label="Lab">{lead.service_intake.lab}</Field>
                ) : null}
                {lead.service_intake?.location_type ? (
                  <Field label="Location Type">{lead.service_intake.location_type}</Field>
                ) : null}
                {lead.notes ? (
                  <div className="col-span-2">
                    <Field label="Special Instructions">
                      <p className="text-sm whitespace-pre-wrap" style={{ color: "#475569" }}>{lead.notes}</p>
                    </Field>
                  </div>
                ) : null}
              </div>
            </Card>

            {/* Uploaded Documents */}
            <Card
              title="Uploaded Documents"
              icon={FileText}
              testId="documents-card"
              action={<span className="text-[11px] font-medium" style={{ color: "#0B2545" }}>View All</span>}
            >
              {lead.lab_order_file_id ? (
                <a
                  href={`/api/leads/${lead.id}/lab-order?token=${encodeURIComponent(getAdminToken() || "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid="document-row"
                >
                  <span className="grid place-items-center h-10 w-10 rounded text-[10px] font-bold"
                        style={{ background: "#FEE2E2", color: "#991B1B" }}>
                    PDF
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>
                      {lead.lab_order_filename || "Lab Order"}
                    </div>
                    <div className="text-[11px]" style={{ color: "#94A3B8" }}>
                      Uploaded {format(new Date(lead.created_at), "MMM d, yyyy · h:mm a")}
                    </div>
                  </div>
                </a>
              ) : (
                <p className="text-sm" style={{ color: "#94A3B8" }}>No documents uploaded.</p>
              )}
            </Card>
          </div>

          {/* MIDDLE COLUMN */}
          <div className="space-y-5">
            <AIPricingCard lead={lead} onApplyPrice={handleLeadUpdate} onModifyPrice={() => setEditSection("billing")} />

            {/* Billing card */}
            <Card title="Billing" icon={DollarSign} testId="billing-card-workspace" action={<EditBtn onClick={() => setEditSection("billing")} disabled={!isAdmin} />}>
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Payment Method</dt>
                  <dd style={{ color: "#0B2545" }}>{lead.payment_status === "paid" ? "Stripe" : "Not Collected"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Payment Status</dt>
                  <dd><PaymentPill value={lead.payment_status || "unpaid"} /></dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Amount</dt>
                  <dd className="font-semibold" style={{ color: "#0B2545" }}>{fmtMoney(lead.amount_due)}</dd>
                </div>
              </dl>
              <div className="mt-4">
                <SendPaymentButton lead={lead} onChange={handleLeadUpdate} size="lg" className="w-full" />
              </div>
              {lead.custom_payment_link ? (
                <div className="mt-2 flex items-center gap-2 rounded border p-2 min-w-0 overflow-hidden"
                     style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
                  <Link2 className="h-3 w-3 shrink-0" style={{ color: "#635BFF" }} />
                  <a href={lead.custom_payment_link} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] truncate underline block min-w-0 flex-1" style={{ color: "#0B2545" }}>
                    {lead.custom_payment_link}
                  </a>
                </div>
              ) : null}
            </Card>

            {/* Internal Notes */}
            <Card title="Internal Notes" testId="internal-notes-card" action={<EditBtn onClick={() => setEditSection("notes")} disabled={!isAdmin} />}>
              {lead.notes ? (
                <div
                  className="rounded-lg p-3 border-l-4"
                  style={{ borderColor: "#D4AF37", background: "rgba(212,175,55,0.06)" }}
                  data-testid="internal-notes-content"
                >
                  <p className="text-sm" style={{ color: "#475569" }}>{lead.notes}</p>
                  <p className="text-[10px] mt-2" style={{ color: "#94A3B8" }}>
                    Added · {lead.created_at ? format(new Date(lead.created_at), "MMM d, yyyy h:mm a") : "—"}
                  </p>
                </div>
              ) : (
                <p className="text-sm" style={{ color: "#94A3B8" }}>No notes yet.</p>
              )}
            </Card>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">
            <TimelineCard lead={lead} />

            <Card title="Summary" testId="summary-card" action={<EditBtn onClick={() => setEditSection("summary")} disabled={!isAdmin} />}>
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Service</dt>
                  <dd className="text-right" style={{ color: "#0B2545" }}>{(lead.services || []).join(", ") || "—"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Date</dt>
                  <dd style={{ color: "#0B2545" }}>{lead.preferred_date || "—"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Time</dt>
                  <dd style={{ color: "#0B2545" }}>{lead.preferred_time || "—"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Location</dt>
                  <dd className="text-right max-w-[60%] truncate" style={{ color: "#0B2545" }}>{lead.service_address || "—"}</dd>
                </div>
                <hr style={{ borderColor: "#E2E8F0" }} />
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Pricing Type</dt>
                  <dd style={{ color: "#0B2545" }}>{lead.pricing_type || "Standard"}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt style={{ color: "#94A3B8" }}>Currency</dt>
                  <dd style={{ color: "#0B2545" }}>{lead.currency || "USD"}</dd>
                </div>
              </dl>
            </Card>
          </div>
        </div>

        {/* COMMUNICATIONS — full-width card below the 3-column grid */}
        <div className="mt-6">
          <Card
            title="Communications"
            icon={Mail}
            testId="communications-card"
          >
            <LeadCommunicationsPanel lead={lead} onLeadUpdate={load} />
          </Card>
        </div>
      </main>

      {/* AI Assistant slide-over */}
      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: "#7A5E12" }} />
              AI Operations Assistant
            </SheetTitle>
            <p className="text-[11px]" style={{ color: "#64748B" }}>
              {requestId} · {lead.full_name}
            </p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-5">
            <OpsAnalysisPanel
              leadId={lead.id}
              onApplyStaff={() => { load(); toast.success("Staff applied."); }}
              onApplyPrice={() => { load(); toast.success("Price applied."); }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <LeadEditDialog
        section={editSection}
        lead={lead}
        open={!!editSection}
        onClose={() => setEditSection(null)}
        onSaved={(updated) => handleLeadUpdate(updated)}
      />
    </div>
  );
}

const EditBtn = ({ onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="text-[11px] font-medium hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
    style={{ color: "#0B2545" }}
    data-testid="card-edit-button"
  >
    <Pencil className="h-3 w-3" />
    Edit
  </button>
);

const EditLink = () => (
  <button className="text-[11px] font-medium hover:underline inline-flex items-center gap-1" style={{ color: "#0B2545" }}>
    <Pencil className="h-3 w-3" />
    Edit
  </button>
);
